import { MaterialColors as KMColors } from "@kaggle/core";
import { Color, SessionState, ThemeName } from "./types";

export const kagglePalette: Color = {
  50: "#ffffff",
  100: "#dedfe0",
  200: "#a5e5ff",
  300: "#5dd0ff",
  400: "#3fc7ff",
  500: "#20beff",
  600: "#01b5ff",
  700: "#00a0e2",
  800: "#008ac3",
  900: "#0075a5",
  A100: "#ffffff",
  A200: "#b9ebff",
  A400: "#53cdff",
  A700: "#39c5ff",
};

interface Box {
  width: string;
  height: string;
}

/** Useful for anything that needs size ranges. */
export interface SizeSet<T extends string | Box> {
  xs: T;
  s: T;
  m: T;
  l: T;
  xl: T;
  xxl: T;
}

interface Font {
  /** Mixin for the entire `font-family: "Font Name";` tag. */
  family: string;
  /** Mixins for the entire `font-weight: 500;` tag. */
  weight: {
    light: string;
    normal: string;
    bold: string;
  };
  /** Mixins for the entire `line-height: 1.5;` tag. */
  height: {
    short: string;
    normal: string;
    tall: string;
  };
  /** Mixins for the entire `font-size: 1em;` tag. */
  size: SizeSet<string>;
}

// TODO(cochamberlain): Move this to styled-components only with v4.
/**
 * The cell spacing configuration.
 */
export const CELL_SPACING = {
  left: `${50}px`,
  right: `${8}px`,
  leftMargin: `${5}px`,
  rightMargin: `${5}px`,
  vertical: `${2.857142}rem`,
  verticalHalf: `${2.857142 / 2}rem`,
  contentVertical: `${2.285714}rem`,
  contentVerticalHalf: `${2.285714 / 2}rem`,
};

interface StandardType {
  fontFamily?: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: string;
}

interface Brand {
  900: string;
  800: string;
  700: string;
  600: string;
  500: string;
  400: string;
  300: string;
  200: string;
  100: string;
  50: string;
}

interface Medal {
  dark: string;
  medium: string;
  light: string;
}
interface Status {
  1000: string;
  900: string;
  700: string;
  500: string;
  300: string;
  100: string;
}
interface Tier {
  1000: string;
  900: string;
  700: string;
  500: string;
}

interface ThemeElevation {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
}

interface Elevation {
  light: ThemeElevation;
  dark: ThemeElevation;
}

export interface Theme {
  name: ThemeName;
  session: SessionState;
  font: {
    main: Font;
    code: Font;
  };
  material: {
    spacing: (...factors: number[]) => string;
    elevation: Elevation;
    elevationBackground: Elevation;
    typography: {
      fontSize: { [key: number]: string };
      fontWeight: {
        normal: string;
        medium: string;
      };

      display1: StandardType;
      display2: StandardType;
      display3: StandardType;
      display4: StandardType;
      display5: StandardType;
      display6: StandardType;
      h1: StandardType;
      h2: StandardType;
      h3: StandardType;
      h4: StandardType;
      h5: StandardType;
      h6: StandardType;
      subhead: StandardType;
      body1: StandardType;
      body2: StandardType;
      body3: StandardType;
      button: StandardType;
      text: StandardType;
      caption: StandardType;
      overline: {
        fontSize: string;
        fontVariant: string;
        fontWeight: string;
        lineHeight: string;
        textTransform: string;
      };
      code: {
        fontSize: string;
        fontWeight: string;
        fontFamily: string;
        lineHeight: string;
      };
    };
    color: {
      mode: {
        light: {
          alert: {
            notice: string;
            confirm: string;
            warning: string;
            error: string;
          };
          background: string;
          text: {
            high: string;
            medium: string;
            low: string;
            disabled: string;
          };
          textinput: {
            default: string;
            hover: string;
            focused: string;
            error: string;
          };
          radio: {
            default: string;
            hover: string;
            focused: string;
          };
        };
        dark: {
          background: string;
          text: {
            high: string;
            medium: string;
            low: string;
            disabled: string;
          };
          textinput: {
            default: string;
            hover: string;
            focused: string;
            error: string;
          };
          radio: {
            default: string;
            hover: string;
            focused: string;
          };
        };
      };
      anchor: {
        base: string;
        hover: string;
      };
      white: string;
      black: string;
      brand: {
        blue: Brand;
        grey: Brand;
      };
      medal: {
        gold: Medal;
        silver: Medal;
        bronze: Medal;
      };
      status: {
        loading: Status;
        caution: Status;
        error: Status;
        success: Status;
      };
      text: {
        100: string;
        70: string;
        50: string;
        40: string;
      };
      textReverse: {
        100: string;
        70: string;
      };
      tier: {
        novice: Tier;
        kaggler: Tier;
        expert: Tier;
        master: Tier;
        grandmaster: Tier;
      };
    };
    borderRadius: {
      normal: string;
      inset: string;
      large: string;
    };
  };
  colors: {
    primary: string;
    primaryVariant: string;
    secondary: string;
    secondaryVariant: string;
    background: string;
    border: string;
    onPrimary: string;

    /** For existing Kaggle styles (upgrades). */
    kaggle: {
      white: string;
      gray: string;
      blueSuperLight: string;
      blueLightest: string;
      blueLight: string;
      blue: string;
      blueDark: string;
      blueDarkest: string;
      blueInteract: string;
      teal: string;
      red: string;
      redDark: string;
      redDarkest: string;
      blackLight: string;
      black: string;
      green: string;
      orange: string;
    };
    content: {
      borderLight: string;
      border: string;
    };
    text: {
      dark: string;
      medium: string;
      light: string;
      link: string;
    };
    hover: {
      light: string;
      link: string;
    };
    pure: {
      white: string;
      black: string;
    };
    misc: {
      alabaster: "#FAFAFA";
      grayLight: string;
      grayLighter: string;
      grayLightest: string;
    };
  };
  size: {
    /** Responsive breakpoints. */
    breakpoint: {
      screen: {
        mobile: {
          max: string;
        };
        tablet: {
          min: string;
          max: string;
        };
        desktop: {
          min: string;
          max: string;
        };
        desktopLarge: {
          min: string;
        };
      };
    };
    /** Site content sizes. */
    site: {
      width: {
        desktop: string;
        desktopWrapper: string;
        desktopWide: string;
      };
    };
    modal: SizeSet<Box>;
    notebook: {
      cell: typeof CELL_SPACING;
    };
    /** Border sizing. */
    border: {
      radius: {
        button: string;
        chip: string;
        dropdownMenu: string;
        input: string;
        loadingBar: string;
        modal: string;
        radioBubble: string;
        thumbnail: string;
        /** Sidebar pane "widget". */
        widget: string;
      };
    };
    /** Padding sizes (rem) */
    pad: SizeSet<string>;
    /** Margin sizes (rem) */
    margin: SizeSet<string>;
    /** Images */
    img: {
      thumbnail: {
        datasets: {
          width: string;
          height: string;
        };
      };
    };
  };
  layout: {
    notebook: {
      viewerContent: "row" | "row-reverse";
      versionsBorderSide: "left" | "right";
      pageMargin: string;
      listingMargin: string;
    };
    education: {
      mainContentDirection: string;
    };
    site: {
      contentMargin: string;
    };
  };
  zindex: {
    background: number;
    ground: number;
    foreground: number;
    100: 100;
    999: 999;
    1000: 1000;
    1001: 1001;
  };

  shadow: {
    modal: string;
    elevation2: string;
  };
}

const spacing = (...factors: number[]): string => {
  return factors.map(factor => `${factor * 4}px`).join(" ");
};

/** Create a theme for a palette set. */
export function createTheme(
  session: SessionState = {
    themeName: "light",
    sourceType: "notebook",
    experimentalTheme: false,
  }
): Theme {
  return {
    name: session.themeName,
    session,
    material: {
      spacing,
      elevation: {
        light: {
          1: "0 1px 2px 0 rgba(60,64,67,0.30), 0 1px 3px 1px rgba(60,64,67,0.15)",
          2: "0 1px 2px 0 rgba(60,64,67,0.30), 0 2px 6px 2px rgba(60,64,67,0.15)",
          3: "0 1px 3px 0 rgba(60,64,67,0.30), 0 4px 8px 3px rgba(60,64,67,0.15)",
          4: "0 2px 3px 0 rgba(60,64,67,0.30), 0 6px 10px 4px rgba(60,64,67,0.15)",
          5: "0 4px 4px 0 rgba(60,64,67,0.30), 0 8px 12px 6px rgba(60,64,67,0.15)",
        },
        dark: {
          1: "0 1px 2px 0 rgba(0,0,0,0.30), 0 1px 3px 1px rgba(0,0,0,0.15)",
          2: "0 1px 2px 0 rgba(0,0,0,0.30), 0 2px 6px 2px rgba(0,0,0,0.15)",
          3: "0 1px 3px 0 rgba(0,0,0,0.30), 0 4px 8px 3px rgba(0,0,0,0.15)",
          4: "0 2px 3px 0 rgba(0,0,0,0.30), 0 6px 10px 4px rgba(0,0,0,0.15)",
          5: "0 4px 4px 0 rgba(0,0,0,0.30), 0 8px 12px 6px rgba(0,0,0,0.15)",
        },
      },
      elevationBackground: {
        light: {
          1: "transparent",
          2: "transparent",
          3: "transparent",
          4: "transparent",
          5: "transparent",
        },
        dark: {
          1: "rgba(232,234,237,0.05)",
          2: "rgba(232,234,237,0.08)",
          3: "rgba(232,234,237,0.11)",
          4: "rgba(232,234,237,0.12)",
          5: "rgba(232,234,237,0.14)",
        },
      },
      typography: {
        display1: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "64px",
          lineHeight: "76px",
          fontWeight: "700",
        },
        display2: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "56px",
          lineHeight: "64px",
          fontWeight: "700",
        },
        display3: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "44px",
          lineHeight: "52px",
          fontWeight: "700",
        },
        display4: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "36px",
          lineHeight: "44px",
          fontWeight: "700",
        },
        display5: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "28px",
          lineHeight: "36px",
          fontWeight: "700",
        },
        display6: {
          fontFamily: "zeitung, sans-serif",
          fontSize: "24px",
          lineHeight: "32px",
          fontWeight: "700",
        },
        h1: {
          fontSize: "36px",
          lineHeight: "44px",
          fontWeight: "700",
        },
        h2: {
          fontSize: "32px",
          lineHeight: "40px",
          fontWeight: "700",
        },
        h3: {
          fontSize: "28px",
          lineHeight: "36px",
          fontWeight: "700",
        },
        h4: {
          fontSize: "24px",
          lineHeight: "32px",
          fontWeight: "700",
        },
        h5: {
          fontSize: "20px",
          lineHeight: "24px",
          fontWeight: "700",
        },
        h6: {
          fontSize: "16px",
          lineHeight: "20px",
          fontWeight: "700",
        },
        subhead: {
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: "600",
        },
        body1: {
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: "500",
        },
        body2: {
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: "500",
        },
        body3: {
          fontSize: "12px",
          lineHeight: "20px",
          fontWeight: "500",
        },
        button: {
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: "700",
        },
        text: {
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: "500",
        },
        caption: {
          fontSize: "12px",
          lineHeight: "16px",
          fontWeight: "500",
        },
        overline: {
          fontSize: "10px",
          fontVariant: "small-caps",
          lineHeight: "16px",
          textTransform: "lowercase",
          fontWeight: "600",
        },
        code: {
          fontFamily: '"Roboto Mono", monospace',
          fontSize: "14px",
          fontWeight: "400",
          lineHeight: "20px",
        },

        // TODO(jomike) Delete after transition to next
        fontSize: {
          64: "64px",
          56: "56px",
          44: "44px",
          36: "36px",
          32: "32px",
          28: "28px",
          24: "24px",
          20: "20px",
          16: "16px",
          14: "14px",
          12: "12px",
          10: "10px",
        },
        // TODO(jomike) Delete after transition to next
        fontWeight: {
          normal: "400",
          medium: "500",
        },
      },
      color: {
        mode: {
          light: {
            alert: {
              notice: KMColors.BLUE_600,
              confirm: KMColors.GREEN_600,
              warning: KMColors.YELLOW_900,
              error: KMColors.RED_600,
            },
            background: KMColors.KAGGLE_WHITE,
            text: {
              high: KMColors.GREY_900,
              medium: KMColors.GREY_800,
              low: KMColors.GREY_700,
              disabled: KMColors.GREY_600,
            },
            textinput: {
              default: KMColors.GREY_300,
              hover: KMColors.GREY_900,
              focused: KMColors.GREY_900,
              error: KMColors.RED_600,
            },
            radio: {
              default: KMColors.GREY_900,
              hover: KMColors.GREY_900,
              focused: KMColors.GREY_900,
            },
          },
          dark: {
            background: KMColors.GREY_900,
            text: {
              high: KMColors.GREY_200,
              medium: KMColors.GREY_500,
              low: KMColors.GREY_600,
              disabled: KMColors.GREY_700,
            },
            textinput: {
              default: "#89868B",
              hover: KMColors.GREY_50,
              focused: KMColors.GREY_50,
              error: KMColors.RED_600,
            },
            radio: {
              default: KMColors.GREY_50,
              hover: KMColors.GREY_50,
              focused: KMColors.GREY_50,
            },
          },
        },
        anchor: {
          base: "#008ABC",
          hover: KMColors.KAGGLE_BLUE,
        },
        white: "#fff",
        black: "#000",
        brand: {
          blue: {
            900: "#007BA8",
            800: "#008ABC",
            700: "#04A4DE",
            600: "#0CB1ED",
            500: "#20BEFF",
            400: "#79D9FF",
            300: "#A5E5FF",
            200: "#D2F2FF",
            100: "#E8F8FF",
            50: "#F6FDFF",
          },
          grey: {
            900: "#47494D",
            800: "#595B5E",
            700: "#6C6D71",
            600: "#7E7F82",
            500: "#919294",
            400: "#B5B6B8",
            300: "#C7C8C9",
            200: "#DEDFE0",
            100: "#ECECED",
            50: "#F8F8F8",
          },
        },
        medal: {
          gold: {
            dark: "#E79F50",
            medium: "#F9B871",
            light: "#FAC68D",
          },
          silver: {
            dark: "#B4B4B4",
            medium: "#D2D2D2",
            light: "#DFDFDF",
          },
          bronze: {
            dark: "#A2745B",
            medium: "#C08B6C",
            light: "#CDA289",
          },
        },
        status: {
          loading: {
            1000: "#85670F",
            900: "#EBB61A",
            700: "#F7BE14",
            500: "#F9CF51",
            300: "#FAE196",
            100: "#F9EFD0",
          },
          caution: {
            1000: "#844913",
            900: "#F28218",
            700: "#F29135",
            500: "#FDAA5C",
            300: "#F5C293",
            100: "#F5D7BB",
          },
          error: {
            1000: "#803433",
            900: "#DA5957",
            700: "#F26361",
            500: "#F58B8A",
            300: "#F2ACAA",
            100: "#F3CDCC",
          },
          success: {
            1000: "#008665",
            900: "#00C090",
            700: "#00D9A3",
            500: "#42E3BB",
            300: "#84F0D5",
            100: "#B9EEE1",
          },
        },
        text: {
          100: "rgba(0,0,0,1)",
          70: "rgba(0,0,0,0.7)",
          50: "rgba(0,0,0,0.5)",
          40: "rgba(0,0,0,0.4)",
        },
        textReverse: {
          100: "rgba(255,255,255,1)",
          70: "rgba(255,255,255,0.7)",
        },
        tier: {
          novice: {
            1000: "#006E52",
            900: "#3EBF9F",
            700: "#42E3BB",
            500: "#84F0D5",
          },
          kaggler: {
            1000: "#007BA8",
            900: "#04A4DE",
            700: "#20BEFF",
            500: "#A5E5FF",
          },
          expert: {
            1000: "#543350",
            900: "#734D78",
            700: "#9A5692",
            500: "#C08FBA",
          },
          master: {
            1000: "#753316",
            900: "#C15224",
            700: "#F76629",
            500: "#FAA37E",
          },
          grandmaster: {
            1000: "#735B19",
            900: "#AD8A26",
            700: "#DEAC14",
            500: "#EECB67",
          },
        },
      },
      borderRadius: {
        normal: "4px",
        inset: "3px",
        large: "8px",
      },
    },
    colors: {
      primary: "#20beff",
      primaryVariant: "#5dd0ff",
      secondary: "#53cdff",
      secondaryVariant: "#b9ebff",
      background: "#ffffff",
      border: "#dedfe0",
      onPrimary: "#008ac3",

      kaggle: {
        white: "#fff",
        gray: "#a9a9a9",
        blueSuperLight: "#ebf9ff",
        blueLightest: "#e1f0f6",
        blueLight: "#86dcff",
        blue: "rgb(32, 190, 255)",
        blueDark: "#008abc",
        blueDarkest: "#006184",
        blueInteract: "#00A7EC",
        teal: "#2AC090",
        red: "#cc615c",
        redDark: "#B34834",
        redDarkest: "#D0021B",
        blackLight: "#6F7175",
        black: "#47494D",
        green: "#69AC35",
        orange: "#F69732",
      },
      content: {
        borderLight: "#ebecec",
        border: "rgb(222, 223, 224)",
      },
      text: {
        dark: "rgba(0,0,0,1)",
        medium: "rgba(0,0,0,0.7)",
        light: "rgba(0,0,0,0.4)",
        link: "#008ABC",
      },
      hover: {
        light: "rgba(0,0,0,0.03)",
        link: "rgb(32, 190, 255)",
      },
      pure: {
        white: "#fff",
        black: "#000",
      },
      misc: {
        alabaster: "#FAFAFA",
        grayLight: "#B3B3B3",
        grayLighter: "#C4C9CD",
        grayLightest: "#F0F0F0",
      },
    },
    font: {
      main: {
        family: 'font-family: "Inter", sans-serif',
        weight: {
          light: "font-weight: 400",
          normal: "font-weight: 500",
          bold: "font-weight: 700",
        },
        size: {
          xs: "font-size: 0.7em",
          s: "font-size: 0.8em",
          m: "font-size: 1em",
          l: "font-size: 1.2em",
          xl: "font-size: 1.3em",
          xxl: "font-size: 1.5em",
        },
        height: {
          short: "line-height: 1.2",
          normal: "line-height: 1.375",
          tall: "line-height: 1.5",
        },
      },
      code: {
        family: 'font-family: "Roboto Mono", monospace',
        weight: {
          light: "font-weight: 400",
          normal: "font-weight: 500",
          bold: "font-weight: 700",
        },
        size: {
          xs: "font-size: 0.7em",
          s: "font-size: 0.8em",
          m: "font-size: 1em",
          l: "font-size: 1.2em",
          xl: "font-size: 1.3em",
          xxl: "font-size: 1.5em",
        },
        height: {
          short: "line-height: 1.2",
          normal: "line-height: 1.375",
          tall: "line-height: 1.5",
        },
      },
    },
    size: {
      border: {
        radius: {
          button: "4px",
          chip: "16px",
          dropdownMenu: "2px",
          input: "4px",
          loadingBar: "4px",
          modal: "4px",
          radioBubble: "14px",
          thumbnail: "4px",
          widget: "2px",
        },
      },
      pad: {
        xs: "3px",
        s: "6px",
        m: "12px",
        l: "18px",
        xl: "24px",
        xxl: "30px",
      },
      margin: {
        xs: "3px",
        s: "6px",
        m: "12px",
        l: "18px",
        xl: "24px",
        xxl: "30px",
      },
      img: {
        thumbnail: {
          datasets: {
            width: "64px",
            height: "64px",
          },
        },
      },
      site: {
        width: {
          desktop: "950px",
          desktopWrapper: "990px",
          desktopWide: "90vw",
        },
      },
      modal: {
        xs: { width: "0px", height: "0px" },
        s: { width: "400px", height: "300px" },
        m: { width: "0px", height: "0px" },
        l: { width: "740px", height: "520px" },
        xl: { width: "0px", height: "0px" },
        xxl: { width: "0px", height: "0px" },
      },
      notebook: {
        cell: CELL_SPACING,
      },
      breakpoint: {
        screen: {
          mobile: {
            max: "480px",
          },
          tablet: {
            min: "481px",
            max: "768px",
          },
          desktop: {
            min: "769px",
            max: "1024px",
          },
          desktopLarge: {
            min: "1025px",
          },
        },
      },
    },
    layout: {
      notebook: {
        viewerContent: "row",
        versionsBorderSide: "right",
        pageMargin: "16px",
        listingMargin: "0px auto 32px",
      },
      education: {
        mainContentDirection: "row",
      },
      site: {
        contentMargin: "0px auto",
      },
    },
    zindex: {
      background: 0,
      ground: 1,
      foreground: 2,
      100: 100,
      999: 999,
      1000: 1000,
      1001: 1001,
    },
    shadow: {
      modal:
        "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
      elevation2:
        "0 1px 2px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15)",
    },
  };
}

export const defaultTheme = createTheme();

function createNavigationTheme() {
  const theme = createTheme({
    ...defaultTheme.session,
    experimentalTheme: true,
  });
  theme.layout.notebook.viewerContent = "row-reverse";
  theme.layout.notebook.versionsBorderSide = "left";
  theme.layout.notebook.pageMargin = "0px";
  theme.layout.notebook.listingMargin = "0 auto 32px 0";
  theme.layout.education.mainContentDirection = "row-reverse";
  theme.layout.site.contentMargin = "0px auto 0px 48px";
  return theme;
}

export const navigationTheme = createNavigationTheme();
